import './DashboardLayout.css';
import { Outlet, useLocation } from 'react-router-dom';
import DashboardMenu from './dashboard_layout/DashboardMenu';
import DashboardCurtain from './dashboard_layout/DashboardCurtain';
import { useContext, useEffect, useState } from 'react';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import DashboardHeader from './dashboard_layout/DashboardHeader';
import {
  DESKTOP_MENU_WIDTH,
  EnvironmentBanner,
  FLOATING_MENU_WIDTH,
  getEnvironmentBannerMode,
} from '@leedpulse-monorepo/components';
import classNames from 'classnames';
import { SIGN_IN_PAGE_PATH } from '../pages/SignInPage';
import AuthenticationContext, {
  AuthenticationState,
} from '../contexts/auth/AuthenticationContext';
import LoadingCurtain from '../components/@shared/LoadingCurtain';
import AppContext from '../contexts/data/AppContext';
import PageScaffoldContext from '../contexts/ui/PageScaffoldContext';
import LogsMenu from '../components/dashboardmenu/LogsMenu';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_ROUTES } from '../AppRouter';
import { metadata } from '@leedpulse-monorepo/firebase-metadata';
import AdminsMenu from '../components/dashboardmenu/AdminsMenu';

const LOCAL_STORAGE_MENU_COLLAPSED_KEY = 'leedpulse-backoffice-menu-collapsed';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DashboardLayoutProps { }

const DashboardLayout: React.FC<DashboardLayoutProps> = (_) => {
  const authContext = useContext(AuthenticationContext);
  const appContext = useContext(AppContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const navigate = useNavigate();
  const location = useLocation();
  const windowSize = useWindowSize();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(
    () => localStorage.getItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY) === '1'
  );

  const [isAdminsOpened, setisAdminsOpened] = useState<{open: boolean, tab?: string}>({open: false});
  const [isLogsOpened, setisLogsOpened] = useState<{open: boolean, id?: string}>({open: false});

  useEffect(() => {
    // Skip if windowSize not yet initialized (returns 0 on first render)
    if (windowSize.width === 0) return;

    if (isMobileMenuOpen) {
      // Synchro avec un systeme externe au rendu (DOM, storage, timer, toast,
      // navigation) : l'etat accompagne un effet de bord qui ne peut pas remonter au rendu.
      // eslint-disable-next-line react/set-state-in-effect
      setMobileMenuOpen(false);
    }
    if (windowSize.width < FLOATING_MENU_WIDTH && !isMenuCollapsed) {
      setIsMenuCollapsed(true);
    } else if (windowSize.width > DESKTOP_MENU_WIDTH && isMenuCollapsed) {
      const localValue = localStorage.getItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY) === '1';
      setIsMenuCollapsed(localValue);
    }
  }, [windowSize.width]);

  useEffect(() => {
    if (
      authContext.authenticationState === AuthenticationState.UNAUTHENTICATED &&
      !PUBLIC_ROUTES.some((route) => route.path === location.pathname)
    ) {
      navigate(SIGN_IN_PAGE_PATH);
    }
  }, [authContext.authenticationState]);

  useEffect(() => {
    appContext.fetch();
  }, []);

  useEffect(() => {
    const pageScaffoldContent = pageScaffoldContext?.contentRef.current;
    if (!pageScaffoldContent || windowSize.width < FLOATING_MENU_WIDTH) {
      return;
    }
    // Le menu est `position: fixed` à `left: 8px` : replié il se termine à 88px,
    // déplié à 296px (8 + 288). Le padding gauche vaut donc cette largeur PLUS
    // la gouttière, et doit rester égal aux 32px des trois autres côtés — à
    // 96px et 304px il n'en restait que 8, et le contenu venait coller le menu.
    // Ces valeurs priment sur celles de PageScaffold.css : les changer ici
    // seulement laisserait la feuille de style mentir.
    // Ecriture directe dans le DOM : c'est bien une synchro avec un systeme
    // externe, l'endroit prevu pour un effet. react/immutability voit une
    // mutation d'objet sans pouvoir distinguer un noeud DOM d'un state React.
    // eslint-disable-next-line react/immutability
    pageScaffoldContent.style.padding = isMenuCollapsed
      ? '32px 32px 32px 120px'
      : '32px 32px 32px 328px';
  }, [
    isMenuCollapsed,
    location.pathname,
    pageScaffoldContext?.contentRef,
    windowSize.width,
    authContext.authenticationState,
  ]);

  return authContext.authenticationState !==
    AuthenticationState.AUTHENTICATED ? (
    <LoadingCurtain />
  ) : (
    <div className="dashboard-layout">
      <EnvironmentBanner mode={getEnvironmentBannerMode(metadata('backoffice'))} />
      <DashboardMenu
        show={isMobileMenuOpen}
        isCollapsed={isMenuCollapsed}
        onNavigate={() => setMobileMenuOpen(false)}
        onCollapseChange={(value) => {
          setIsMenuCollapsed(value)
          localStorage.setItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY, value ? '1' : '0');
        }}
        onAdminsOpen={(tab?: string) => setisAdminsOpened({open: true, tab})}
        onLogsOpen={(id?: string) => setisLogsOpened({open: true, id})}
      />
      <DashboardCurtain
        show={isMobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />
      {
        windowSize.width < FLOATING_MENU_WIDTH && <DashboardHeader onMenuClick={() => setMobileMenuOpen(true)} />
      }

      <AdminsMenu isAdminsOpened={isAdminsOpened} onClose={() => setisAdminsOpened({open: false, tab: undefined})} />
      <LogsMenu isLogsOpened={isLogsOpened} onClose={() => setisLogsOpened({open: false, id: undefined})} />

      <main
        className={classNames('dashboard-layout__content', {
          'dashboard-layout__content--collapsed': isMenuCollapsed,
        })}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
